import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BILLING_PERIOD_LABELS,
  type BillingPeriod,
  type ProductSpec,
  type RateView,
  type WorkbenchProduct,
} from '@/types/product';

import WorkbenchHoursGrid from './WorkbenchHoursGrid';
import { duplicateLastVariant, removeVariant } from './productEditorState';
import MoneyInput from './WorkbenchMoneyInput';
import { money } from './workbenchPricing';
import type { ProductWorkbench } from '@/types/product';

interface Props {
  product: WorkbenchProduct & { changed: boolean; edited: boolean };
  baseline: WorkbenchProduct;
  open: boolean;
  busy: boolean;
  rates: RateView[];
  ratesByKey: Record<string, number>;
  data: ProductWorkbench;
  showDifferences: boolean;
  onToggle: () => void;
  onChange: (spec: ProductSpec) => void;
  onBillingPeriod: (value: BillingPeriod) => void;
  onDetail: () => void;
  onDuplicate: () => void;
  onPublish: () => void;
  hasRateChanges: boolean;
  onStage: () => void;
  onSelectCell: (cell: { productId: string; lineId: string; variantId: string }) => void;
}

export default function WorkbenchProductRow({
  product,
  baseline,
  open,
  busy,
  rates,
  ratesByKey,
  data,
  showDifferences,
  onToggle,
  onChange,
  onBillingPeriod,
  onDetail,
  onDuplicate,
  onPublish,
  hasRateChanges,
  onStage,
  onSelectCell,
}: Props) {
  const { spec, computation } = product;
  const scroller = useRef<HTMLDivElement>(null);
  const previousVariantCount = useRef(spec.variants.length);
  const newVariantAdded = useRef(false);
  const [deleteVariantId, setDeleteVariantId] = useState<string | null>(null);
  const variantToDelete = spec.variants.find((variant) => variant.id === deleteVariantId);
  const focusAfterDeletion = useRef<number | null>(null);
  useEffect(() => {
    if (spec.variants.length > previousVariantCount.current) {
      const input = scroller.current
        ?.querySelectorAll<HTMLInputElement>('input[data-variant-name]')
        .item(spec.variants.length - 1);
      input?.focus({ preventScroll: true });
      input?.select();
      scroller.current?.scrollTo({ left: scroller.current.scrollWidth });
    }
    previousVariantCount.current = spec.variants.length;
  }, [spec.variants.length]);
  const columns = `minmax(300px, 2fr) repeat(${spec.variants.length}, minmax(150px, 1fr)) 44px`;
  const width = 344 + spec.variants.length * 150;
  const setFixed = (variantId: string, cents: number | null) => {
    const prices = { ...spec.fixedPriceCents };
    if (cents === null) delete prices[variantId];
    else prices[variantId] = cents;
    onChange({ ...spec, fixedPriceCents: prices });
  };
  return (
    <section aria-label={product.name} className="border-b last:border-b-0">
      <div ref={scroller} className="overflow-x-auto">
        <div style={{ minWidth: width }}>
          <div
            className={`grid min-h-16 items-center ${open ? 'bg-secondary' : 'bg-card'}`}
            style={{ gridTemplateColumns: columns }}
          >
            <div className="min-w-0 px-4 py-2">
              <button
                type="button"
                disabled={busy}
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`price-build-${product.id}`}
                className="flex min-h-7 w-full min-w-0 items-center gap-2 rounded text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info"
              >
                {open ? (
                  <ChevronDown className="h-4 w-4 shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 text-sm font-semibold">{product.name}</span>
              </button>
              <div className="ml-6 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                <Select
                  disabled={busy}
                  value={product.billingPeriod}
                  onValueChange={(value) => onBillingPeriod(value as BillingPeriod)}
                >
                  <SelectTrigger
                    aria-label={`${product.name} · facturatieperiode`}
                    className="-ml-1 h-6 w-auto max-w-full gap-1 border-transparent px-1 text-xs hover:border-input focus:border-input [&>svg]:h-3 [&>svg]:w-3"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BILLING_PERIOD_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>
                  {spec.priceMode === 'fixed'
                    ? ' · vaste prijs'
                    : spec.priceMode === 'on_request'
                      ? ' · op aanvraag'
                      : ''}
                  {!product.enabled
                    ? ' · uitgeschakeld'
                    : !product.published && !product.edited
                      ? ' · concept'
                      : product.hasDraft && !product.edited
                        ? ' · concept open'
                        : ''}
                </span>
                {product.edited && (
                  <span
                    className="ml-1 inline-flex items-center gap-1.5 font-medium text-foreground"
                    title="Deze wijzigingen staan alleen in deze browser. Publiceer ze onderaan de pagina."
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                    Niet gepubliceerd
                  </span>
                )}
              </div>
            </div>
            {spec.variants.map((variant, index) => {
              const computed = computation.variants.find((item) => item.variantId === variant.id)!;
              const before = baseline.computation.variants.find(
                (item) => item.variantId === variant.id
              )?.priceCents;
              const changed =
                showDifferences &&
                before != null &&
                computed.priceCents != null &&
                before !== computed.priceCents;
              return (
                <div key={variant.id} className="group/variant min-w-0 px-4 py-2 text-right">
                  <div className="relative mb-0.5">
                    <Input
                      data-variant-name
                      aria-label={`${product.name} · variant ${index + 1} · naam`}
                      value={variant.name}
                      disabled={busy}
                      aria-invalid={!variant.name.trim()}
                      placeholder="Variantnaam"
                      title="Variantnaam wijzigen"
                      className="h-6 min-w-0 border-transparent px-0 text-right text-xs text-muted-foreground hover:border-input focus-visible:border-input aria-[invalid=true]:border-destructive"
                      onChange={(event) =>
                        onChange({
                          ...spec,
                          variants: spec.variants.map((item) =>
                            item.id === variant.id ? { ...item, name: event.target.value } : item
                          ),
                        })
                      }
                    />
                    <span className="pointer-events-none absolute -left-6 top-0 opacity-0 group-hover/variant:pointer-events-auto group-hover/variant:opacity-100 group-focus-within/variant:pointer-events-auto group-focus-within/variant:opacity-100">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                        aria-label={`${product.name} · variant ${index + 1} · ${variant.name || 'Naamloze variant'} verwijderen`}
                        title={
                          spec.variants.length <= 1
                            ? 'Een product moet minstens één variant hebben'
                            : 'Variant verwijderen'
                        }
                        disabled={busy || spec.variants.length <= 1}
                        onClick={() => setDeleteVariantId(variant.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  </div>
                  {!variant.name.trim() && (
                    <p className="text-xs text-destructive">Vul een variantnaam in.</p>
                  )}
                  {spec.priceMode === 'fixed' && spec.cost.mode !== 'markup' ? (
                    <MoneyInput
                      label={`${product.name} · ${variant.name} · vaste prijs`}
                      amount={spec.fixedPriceCents?.[variant.id]}
                      disabled={busy}
                      onChange={(cents) => setFixed(variant.id, cents)}
                    />
                  ) : computed.errors.length ? (
                    <span className="text-xs font-medium text-destructive">
                      Controleer prijsopbouw
                    </span>
                  ) : (
                    <div className="text-base font-semibold tabular-nums">
                      {spec.priceMode === 'on_request'
                        ? spec.onRequestText || 'Op aanvraag'
                        : money(computed.priceCents)}
                    </div>
                  )}
                  {changed && (
                    <div className="mt-0.5 text-xs tabular-nums">
                      <span className="text-muted-foreground line-through">{money(before)}</span>{' '}
                      <span className="font-semibold text-[hsl(var(--info))]">
                        {computed.priceCents! - before! >= 0 ? '+' : '−'}
                        {money(Math.abs(computed.priceCents! - before!))}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={busy}
                  aria-label={`Acties voor ${product.name}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onCloseAutoFocus={(event) => {
                  if (!newVariantAdded.current) return;
                  event.preventDefault();
                  newVariantAdded.current = false;
                  requestAnimationFrame(() => {
                    const inputs = scroller.current?.querySelectorAll<HTMLInputElement>(
                      'input[data-variant-name]'
                    );
                    const input = inputs?.item(inputs.length - 1);
                    input?.focus({ preventScroll: true });
                    input?.select();
                    scroller.current?.scrollTo({ left: scroller.current.scrollWidth });
                  });
                }}
              >
                <DropdownMenuItem
                  onSelect={() => {
                    newVariantAdded.current = true;
                    onChange(duplicateLastVariant(spec));
                  }}
                >
                  Variant toevoegen
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onDetail}>Productdetails</DropdownMenuItem>
                <DropdownMenuItem onSelect={onDuplicate}>Product dupliceren</DropdownMenuItem>
                <DropdownMenuItem
                  disabled={hasRateChanges || (!product.hasDraft && !product.edited)}
                  onSelect={onPublish}
                  className="flex-col items-start"
                >
                  Product publiceren
                  {hasRateChanges && (
                    <span className="text-xs">Publiceer eerst je tariefwijzigingen</span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onToggle}>
                  {open ? 'Prijsopbouw sluiten' : 'Prijsopbouw bekijken'}
                </DropdownMenuItem>
                {product.hasDraft && !product.edited && (
                  <DropdownMenuItem onSelect={onStage}>
                    Concept klaarzetten om te publiceren
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {open && (
            <div id={`price-build-${product.id}`}>
              <div
                className={`flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 text-xs ${product.edited ? 'border-amber-200 bg-amber-50' : 'bg-muted/50'}`}
              >
                <label className="flex items-center gap-2">
                  Prijs berekenen met
                  <Select
                    disabled={busy}
                    value={spec.priceMode}
                    onValueChange={(value) => {
                      const priceMode = value as ProductSpec['priceMode'];
                      onChange({
                        ...spec,
                        priceMode,
                        ...(priceMode === 'on_request' && !spec.onRequestText
                          ? { onRequestText: 'Op aanvraag' }
                          : {}),
                        ...(priceMode === 'fixed' && !spec.fixedPriceCents
                          ? {
                              fixedPriceCents: Object.fromEntries(
                                computation.variants.map((variant) => [
                                  variant.variantId,
                                  variant.priceCents ?? 0,
                                ])
                              ),
                            }
                          : {}),
                      });
                    }}
                  >
                    <SelectTrigger
                      aria-label={`Prijssoort van ${product.name}`}
                      className="h-8 w-40 bg-card"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Uren × tarief</SelectItem>
                      <SelectItem value="fixed">Vaste prijs</SelectItem>
                      <SelectItem value="on_request">Op aanvraag</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                {/* Staat altijd in de DOM, zodat schermlezers de melding bij de eerste wijziging voorlezen. */}
                <div
                  role="status"
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-amber-950"
                >
                  {product.edited && (
                    <>
                      <span className="flex items-center gap-1.5">
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                          aria-hidden="true"
                        />
                        <span>
                          <strong>Nog niet gepubliceerd.</strong>{' '}
                          {product.published
                            ? 'Nieuwe offertes gebruiken de oude prijs tot je publiceert.'
                            : 'Het product staat pas in offertes als je het publiceert.'}
                        </span>
                      </span>
                      {hasRateChanges ? (
                        <span>Publiceer onderaan, samen met je tariefwijzigingen.</span>
                      ) : (
                        <Button size="sm" className="h-7" disabled={busy} onClick={onPublish}>
                          Publiceren
                        </Button>
                      )}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className="font-semibold text-[hsl(var(--info))] hover:underline"
                  disabled={busy}
                  onClick={onDetail}
                >
                  Productdetails →
                </button>
              </div>
              {spec.priceMode === 'hours' ? (
                <WorkbenchHoursGrid
                  product={product}
                  busy={busy}
                  rates={rates}
                  ratesByKey={ratesByKey}
                  data={data}
                  columns={columns}
                  onChange={onChange}
                  onSelectCell={onSelectCell}
                />
              ) : (
                <div className="px-4 py-4 text-sm text-muted-foreground">
                  {spec.priceMode === 'fixed' ? (
                    spec.cost.mode === 'markup' ? (
                      'Deze prijs volgt uit kostprijs en opslag. Je wijzigt die in Productdetails.'
                    ) : (
                      'Wijzig de vaste prijs direct in de productrij. Tariefwijzigingen veranderen deze prijs niet.'
                    )
                  ) : (
                    <label className="flex items-center gap-3">
                      Tekst voor de offerte
                      <Input
                        className="max-w-xs"
                        value={spec.onRequestText ?? ''}
                        placeholder="Op aanvraag"
                        disabled={busy}
                        onChange={(event) =>
                          onChange({ ...spec, onRequestText: event.target.value })
                        }
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Dialog
        open={Boolean(variantToDelete)}
        onOpenChange={(next) => {
          if (!next) setDeleteVariantId(null);
        }}
      >
        <DialogContent
          onCloseAutoFocus={(event) => {
            const index = focusAfterDeletion.current;
            if (index === null) return;
            event.preventDefault();
            focusAfterDeletion.current = null;
            requestAnimationFrame(() => {
              scroller.current
                ?.querySelectorAll<HTMLInputElement>('input[data-variant-name]')
                .item(index)
                ?.focus({ preventScroll: true });
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>Variant verwijderen?</DialogTitle>
            <DialogDescription>
              Je verwijdert <strong>{variantToDelete?.name || 'Naamloze variant'}</strong> uit{' '}
              <strong>{product.name}</strong>, inclusief omschrijving, uren, prijs en kostprijs. De
              wijziging gaat mee wanneer je publiceert.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteVariantId(null)}>
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={busy || spec.variants.length <= 1}
              onClick={() => {
                if (!variantToDelete) return;
                const index = spec.variants.findIndex(
                  (variant) => variant.id === variantToDelete.id
                );
                focusAfterDeletion.current = Math.min(index, spec.variants.length - 2);
                onChange(removeVariant(spec, variantToDelete.id));
                setDeleteVariantId(null);
              }}
            >
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
